// Per-flavor RUNTIME settings (moved here from src/data so all flavor config
// lives under src/flavors/). Re-exported from src/data for back-compat.
//
// This can't be JSON: it imports SVG assets and references shared tag arrays.
// Primitive build/store config (ids, hosts, revcat) lives in registry.json.

import type { BusinessType, FlavorSetting } from './types'

// These are ICONS — flat single-colour pictograms (steering wheel, lotus, boat), one
// per flavor, at public/flavor_images/<dir>/flavor_default_icon.svg. They are NOT
// business logos; they stand in for a venue that never uploaded one, and they also
// serve as the flavor's own mark (favicon, OG cards). A real per-flavor default
// BUSINESS logo would be a separate, richer asset — see the note on defaultLogo below.
//
// ⚠️ The filename is load-bearing in two places outside this file: the favicon
// fallback in quasar.config.js and scripts/og-cards.mjs both build the path by hand.
// Rename the asset and you must rename it there too.
//
// //FLV_UPDATE — new flavor: add its icon SVG import here. grep FLV_UPDATE for every site.
// `restaurantIcon` is the odd one out: table/tableDDSL use the full-colour DreamDiner
// roundel from /logos/, not a flat pictogram from flavor_images/.
import restaurantIcon from '/logos/dd_logo_round_blue.svg' // Simply make these in TheNoun
import tablePLIcon from '/flavor_images/tablepl/flavor_default_icon.svg'
import carIcon from '/flavor_images/flv01_car/flavor_default_icon.svg'
import roomIcon from '/flavor_images/flv02_room/flavor_default_icon.svg'
import spaIcon from '/flavor_images/flv03_spa/flavor_default_icon.svg'
import nailIcon from '/flavor_images/flv04_nail/flavor_default_icon.svg'
import planeIcon from '/flavor_images/flv11_plane/flavor_default_icon.svg'
import courtIcon from '/flavor_images/flv10_court/flavor_default_icon.svg'
import officeIcon from '/flavor_images/flv09_office/flavor_default_icon.svg'
import dentIcon from '/flavor_images/flv08_dent/flavor_default_icon.svg'
import hairIcon from '/flavor_images/flv07_hair/flavor_default_icon.svg'
import yachtIcon from '/flavor_images/flv05_yacht/flavor_default_icon.svg'
import monsterIcon from '/flavor_images/monster/flavor_default_icon.svg'

// Reservation-tag MDI icons, PER FLAVOR. Each array's order MUST match that
// flavor's labels in `flavors.<flavor>.tableReservationTags.tag01..06` (locale
// files). Positions 01/03/04/06 are shared (Smokers · Pet · Baby · Accessibility);
// positions 02 & 05 differ per flavor — keep icon and label in sync. //FLV_UPDATE (add TAGS_<FLV>)
const TAGS_TABLE = ['mdi-smoking', 'mdi-party-popper',   'mdi-paw', 'mdi-baby-carriage', 'mdi-allergy',   'mdi-wheelchair-accessibility'] // …Event · Allergy
const TAGS_CAR   = ['mdi-smoking', 'mdi-crosshairs-gps', 'mdi-paw', 'mdi-baby-carriage', 'mdi-snowflake', 'mdi-wheelchair-accessibility'] // …GPS · Snow tires
const TAGS_ROOM  = ['mdi-smoking', 'mdi-binoculars',     'mdi-paw', 'mdi-baby-carriage', 'mdi-crown',     'mdi-wheelchair-accessibility'] // …With view · VIP
// Spa (owner, 2026-09-28): Couples · Aromatherapy · Pregnancy · Hydrotherapy · VIP · Accessibility.
// ⚠️ Tags are stored BY POSITION (reservation_icon_tags = 6 booleans), so the three kept
// tags (Aromatherapy 2, VIP 5, Accessibility 6) stayed in their slots — moving them would
// silently relabel every spa booking already tagged. The new ones took the freed slots
// (Smokers → Couples, With a pet → Pregnancy, With baby → Hydrotherapy).
const TAGS_SPA   = ['mdi-account-heart', 'mdi-leaf', 'mdi-human-pregnant', 'mdi-water', 'mdi-crown', 'mdi-wheelchair-accessibility']
// Nail (owner, 2026-10-15): Gel polish · Nail art · Nail extensions · Pedicure · VIP service · Accessibility.
// ⚠️ Stored BY POSITION (see TAGS_SPA): Nail art 2, VIP 5 and Accessibility 6 kept their slots;
// the new ones took the freed slots (Smokers → Gel polish, With a pet → Nail extensions,
// With baby → Pedicure).
const TAGS_NAIL  = ['mdi-bottle-tonic', 'mdi-brush', 'mdi-creation', 'mdi-foot-print', 'mdi-crown', 'mdi-wheelchair-accessibility']
const TAGS_PLANE = ['mdi-airplane', 'mdi-account-tie', 'mdi-compass', 'mdi-airplane-takeoff', 'mdi-school', 'mdi-weather-cloudy'] // Pilot Lessons · Pilot Certification
const TAGS_COURT = ['mdi-tennis-ball', 'mdi-table-tennis', 'mdi-badminton', 'mdi-trophy', 'mdi-account-group', 'mdi-bullseye-arrow'] // Padel · Doubles
const TAGS_OFFICE = ['mdi-handshake', 'mdi-briefcase', 'mdi-video', 'mdi-calendar-check', 'mdi-account-group', 'mdi-bullseye-arrow'] // Consultations · Team Meetings
const TAGS_DENT = ['mdi-tooth', 'mdi-creation', 'mdi-emoticon-happy', 'mdi-toothbrush', 'mdi-human-child', 'mdi-alarm-light'] // Teeth Whitening · Child patient
const TAGS_HAIR = ['mdi-content-cut', 'mdi-palette', 'mdi-hair-dryer', 'mdi-creation', 'mdi-ring', 'mdi-leaf'] // Haircuts · Hair Treatments
// Yacht (owner, 2026-10-15): Sailing trip · Private charter · With captain · Sunset cruise ·
// VIP experience · Accessibility. ⚠️ Stored BY POSITION (see TAGS_SPA): VIP (was Luxury) 5 and
// Accessibility 6 kept their slots; Smokers → Sailing trip, Fishing → Private charter,
// With a pet → With captain, With baby → Sunset cruise.
const TAGS_YACHT = ['mdi-sail-boat', 'mdi-ferry', 'mdi-account-tie-hat', 'mdi-weather-sunset', 'mdi-crown', 'mdi-wheelchair-accessibility']

// Per-flavor UNIT ORNAMENTS — the optional icon a business attaches to a single
// unit (stored as unit_icon). `key` → i18n `unitOrnaments.<key>` (en/he). Keys are
// shared where the concept repeats across flavors (balcony/accessible/luxury/…). //FLV_UPDATE (add ORNAMENTS_<FLV>)
const ORNAMENTS_TABLE = [
  { icon: 'mdi-balcony',        key: 'balcony' },
  { icon: 'mdi-umbrella-beach', key: 'outside' },
  { icon: 'mdi-sofa-outline',   key: 'indoor' },
  { icon: 'mdi-crown',          key: 'vip' },
  { icon: 'mdi-circle-outline', key: 'round' },
  { icon: 'mdi-binoculars',     key: 'view' },
]
const ORNAMENTS_CAR = [
  { icon: 'mdi-car-hatchback',   key: 'economy' },
  { icon: 'mdi-car-estate',      key: 'family' },
  { icon: 'mdi-crown',           key: 'luxury' },
  { icon: 'mdi-car-electric',    key: 'electric' },
  { icon: 'mdi-car-convertible', key: 'convertible' },
  { icon: 'mdi-terrain',         key: 'offRoad' },
]
const ORNAMENTS_ROOM = [
  { icon: 'mdi-waves',                    key: 'seaView' },
  { icon: 'mdi-balcony',                  key: 'balcony' },
  { icon: 'mdi-crown',                    key: 'suite' },
  { icon: 'mdi-pool',                     key: 'poolView' },
  { icon: 'mdi-home-floor-g',             key: 'groundFloor' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_SPA = [
  { icon: 'mdi-spa-outline',              key: 'treatment' },
  { icon: 'mdi-hot-tub',                  key: 'jacuzzi' },
  { icon: 'mdi-flower-tulip',             key: 'aromatherapy' },
  { icon: 'mdi-hand-heart',               key: 'massage' },
  { icon: 'mdi-candle',                   key: 'relaxation' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_NAIL = [
  { icon: 'mdi-hand-back-left',           key: 'manicure' },
  { icon: 'mdi-foot-print',               key: 'pedicure' },
  { icon: 'mdi-brush',                    key: 'gelPolish' },
  { icon: 'mdi-star-four-points',         key: 'nailArt' },
  { icon: 'mdi-water',                    key: 'spaTreatment' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_PLANE = [
  { icon: 'mdi-airplane',          key: 'singleEngine' },
  { icon: 'mdi-airplane-plus',     key: 'twinEngine' },
  { icon: 'mdi-monitor-dashboard', key: 'glassCockpit' },
  { icon: 'mdi-weather-fog',       key: 'ifrCertified' },
  { icon: 'mdi-gamepad-variant',   key: 'simulator' },
  { icon: 'mdi-weather-night',     key: 'nightRated' },
]
const ORNAMENTS_COURT = [
  { icon: 'mdi-home-roof',                key: 'indoor' },
  { icon: 'mdi-lightbulb-on',             key: 'floodlights' },
  { icon: 'mdi-texture-box',              key: 'clay' },
  { icon: 'mdi-square-outline',           key: 'hardCourt' },
  { icon: 'mdi-tennis',                   key: 'equipmentRental' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_HAIR = [
  { icon: 'mdi-face-man',                 key: 'mensCuts' },
  { icon: 'mdi-face-woman',               key: 'womensCuts' },
  { icon: 'mdi-palette',                  key: 'colourSpecialist' },
  { icon: 'mdi-mustache',                 key: 'beardTrim' },
  { icon: 'mdi-human-child',              key: 'kidsCuts' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_OFFICE = [
  { icon: 'mdi-scale-balance',            key: 'lawyer' },
  { icon: 'mdi-calculator',               key: 'accountant' },
  { icon: 'mdi-finance',                  key: 'financialAdvisor' },
  { icon: 'mdi-lightbulb-on',             key: 'consultant' },
  { icon: 'mdi-video',                    key: 'onlineMeeting' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_DENT = [
  { icon: 'mdi-toothbrush',               key: 'hygiene' },
  { icon: 'mdi-emoticon-happy',           key: 'orthodontics' },
  { icon: 'mdi-human-child',              key: 'pediatric' },
  { icon: 'mdi-radiology-box',            key: 'xray' },
  { icon: 'mdi-sleep',                    key: 'sedation' },
  { icon: 'mdi-wheelchair-accessibility', key: 'accessible' },
]
const ORNAMENTS_YACHT = [
  { icon: 'mdi-weather-sunny', key: 'sundeck' },
  { icon: 'mdi-bed',           key: 'cabin' },
  { icon: 'mdi-ferry',         key: 'flybridge' },
  { icon: 'mdi-crown',         key: 'luxury' },
  { icon: 'mdi-fish',          key: 'fishing' },
  { icon: 'mdi-speedometer',   key: 'speed' },
]
const ORNAMENTS_MONSTER = [
  { icon: 'mdi-fire',      key: 'fire' },
  { icon: 'mdi-snowflake', key: 'ice' },
  { icon: 'mdi-flask',     key: 'poison' },
  { icon: 'mdi-flash',     key: 'electric' },
  { icon: 'mdi-ghost',     key: 'ghost' },
  { icon: 'mdi-star',      key: 'star' },
]

// Default unit SEAT CAPACITIES, per flavor (SINGLE SOURCE OF TRUTH — the
// CreateBusiness wizard reads these instead of hard-coding seats). 10 values,
// positional, parallel to defaultUnitNames. "seats" is the generic capacity; it
// means guests (table), passengers (car/yacht), persons/beds (room) — the
// field name stays the same across flavors.

// Brand-color fallback = the historical $primary/$secondary/$accent from
// quasar.variables.sass (DreamDiner dark-blue palette). Any flavor without its
// own `theme` (or any missing key) falls back to these. Applied at runtime by
// src/boot/flavor-theme.ts via Quasar setCssVar (overrides --q-primary/…).
export const DEFAULT_THEME = {
  primary:   '#003250', // darkBlue
  secondary: '#73be00', // darkGreen
  accent:    '#ff2644', // coolRed
}

//FLV_UPDATE — new flavor: add its FLAVOR_SETTING entry (copy the 'newflv' template at the bottom).
// ⚠️ A CLONE flavor (one that ships as its own app but IS another flavor's content —
// see FLAVOR_CLONE_OF below) gets NO entry here. It inherits the base flavor's, so the
// two can never drift apart.
const FLAVOR_SETTING_OWN = {
  'table': {
    unit: 'Table',
    order: 'Order',
    plannerTitle: 'Table Planner',
    defaultLogo: restaurantIcon,
    tags: TAGS_TABLE,
    icon: restaurantIcon,
    unitIcon: 'mdi-table-chair',
    unitOrnaments: ORNAMENTS_TABLE,
    publicTypeIconTrans: '/flavor_images/table/type_table_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/table/type_table_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_table_og_512x512_trans.png',
    storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_tabledd_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [15, 30, 60, 90, 120], // Server is numeral 0 1 2 3 4 -> Default is 2 = 60
    toFromPicker: false,
    // coworkerClass: 'TableCoworker',   // restaurant keeps the 3-role model (leave off)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    // defaultUnitNames: [...],   // wizard unit names (omit → table presets)
    defaultUnitSeats: [2, 2, 2, 4, 4, 4, 6, 6, 10, 15], // Bar·Tab·Out·Vip guests
    addUnitPrefix: 'TAB', // "+ Add" unit → TAB1..TAB9, TABA… //FLV_UPDATE
    // demoLogoSet: 'table',   // this IS the default demo-logo set
    theme: { primary: '#003250', secondary: '#73be00', accent: '#beea3c', dotColor: '#ccd9e6', dotBg: '#f1f6fb' }, // DreamDiner blue (+ blue dot-grid)
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  // Exact clone of 'table' (DreamDiner "Bookings"/Bookit variant, app com.dreamdiner.planner).
  // Same Parse classes (Table/Order), settings and tags — diverges only in store identity.
  // Without this entry FLAVOR_SETTING['tableDDSL'] is undefined → the mainboard renders blank.
  'tableDDSL': {
    unit: 'Table',
    order: 'Order',
    plannerTitle: 'Table Planner',
    defaultLogo: restaurantIcon,
    tags: TAGS_TABLE,
    icon: restaurantIcon,
    unitIcon: 'mdi-table-chair',
    unitOrnaments: ORNAMENTS_TABLE,
    publicTypeIconTrans: '/flavor_images/table/type_table_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/table/type_table_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_tableddsl_og_512x512_trans.png',
    storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_tabledd_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [15, 30, 60, 90, 120], // Server is numeral 0 1 2 3 4 -> Default is 2 = 60
    toFromPicker: false,
    defaultUnitSeats: [2, 2, 2, 4, 4, 4, 6, 6, 10, 15], // guests
    addUnitPrefix: 'TAB', // "+ Add" unit → TAB1.. //FLV_UPDATE
    // demoLogoSet omitted → shares 'table' demo-logo set
    theme: { primary: '#003250', secondary: '#73be00', accent: '#beea3c', dotColor: '#ccd9e6', dotBg: '#f1f6fb' }, // DreamDiner blue (+ blue dot-grid)
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  // Exact clone of 'table' (Plannera-branded variant). Same Parse classes
  // (Table/Order), settings and tags — diverges only in store/backend identity.
  'tablePL': {
    unit: 'Table',
    order: 'Order',
    plannerTitle: 'Table Planner',
    defaultLogo: tablePLIcon,
    tags: TAGS_TABLE,
    icon: tablePLIcon,
    unitIcon: 'mdi-table-chair',
    unitOrnaments: ORNAMENTS_TABLE,
    publicTypeIconTrans: '/flavor_images/tablepl/type_tablepl_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/tablepl/type_tablepl_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_tablepl_og_512x512_trans.png',
    storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_tablepl_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [15, 30, 60, 90, 120], // Server is numeral 0 1 2 3 4 -> Default is 2 = 60
    toFromPicker: false,
    // coworkerClass: 'TableCoworker',   // restaurant keeps the 3-role model (leave off)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    // defaultUnitNames: [...],   // wizard unit names (omit → table presets)
    defaultUnitSeats: [2, 2, 2, 4, 4, 4, 6, 6, 10, 15], // guests
    addUnitPrefix: 'TAB', // "+ Add" unit → TAB1.. //FLV_UPDATE
    demoLogoSet: 'tablePL',
    theme: { primary: '#1d144b', secondary: '#453a89', accent: '#e71011', dotColor: '#d6d1e8', dotBg: '#f4f3fa' }, // Plannera purple (+ purple dot-grid)
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  //flv02 car
  'car': {
    // Booking style (owner, 2026-10-27): a business may book By days instead of By time —
    // counted in DAYS with pick-up / return at 09:00 unless it sets its own. See src/utils/stay.ts.
    bookingStyleChoice: true,
    stayUnit: 'day',
    checkInTime: '09:00',
    checkOutTime: '09:00',
    unit: 'CarUnit',
    pricingUnit: 'hour',
    order: 'CarOrder',
    plannerTitle: 'Car Rental Booking Planner',
    defaultLogo: carIcon,
    tags: TAGS_CAR,
    icon: carIcon,
    unitIcon: 'mdi-car',
    // unitIcon: 'mdi-star', // Demo clip 01 (Demo clip 02 lives in src/i18n/en-flavor.ts)
    unitOrnaments: ORNAMENTS_CAR,
    publicTypeIconTrans: '/flavor_images/flv01_car/type_car_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv01_car/type_car_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_car_og_512x512_trans.png',
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_car_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [60, 360, 1440, 2880, 4320], // Server is numeral 0 1 2 3 4 -> Default is 2 = 1440
    toFromPicker: true,
    coworkerClass: 'CarCoworker', // single generic coworker (Waiter-level), see CoworkersDialog
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    defaultUnitNames: ['CAR1', 'CAR2', 'CAR3', 'CAR4', 'CAR5', 'VAN1', 'VAN2', 'TRK1', 'MOT1', 'MOT2'],
    // Passenger capacity per default vehicle (parallel to defaultUnitNames):
    // cars 5 · vans 8 · truck 3 · motorcycles 2. Adjust to taste.
    defaultUnitSeats: [5, 5, 5, 5, 5, 8, 8, 3, 2, 2],
    addUnitPrefix: 'CAR', // "+ Add" unit → CAR1..CAR9, CARA… //FLV_UPDATE
    demoLogoSet: 'car',
    theme: { primary: '#1b2735', secondary: '#f26419', accent: '#fca311', dotColor: '#d3d9e0', dotBg: '#f5f7f9' }, // Graphite + signal orange
    // theme: { primary: '#1d144b', secondary: '#453a89', accent: '#e71011' }, // Plannera purple
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE) uses convection "planner_web_car" for emails in server!
  },
  //flv03 room
  'room': {
    unit: 'RoomUnit',
    pricingUnit: 'day',
    order: 'RoomOrder',
    plannerTitle: 'Room Rental Booking Planner',
    defaultLogo: roomIcon,
    tags: TAGS_ROOM,
    icon: roomIcon,
    unitIcon: 'mdi-bed',
    unitOrnaments: ORNAMENTS_ROOM,
    publicTypeIconTrans: '/flavor_images/flv02_room/type_room_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv02_room/type_room_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_room_og_512x512_trans.png',
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_room_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [60, 360, 1440, 2880, 4320], // Server is numeral 0 1 2 3 4 -> Default is 2 = 1440
    toFromPicker: true,
    // Hotel-style check-in → check-out booking for guests (src/utils/stay.ts). Room ONLY — the
    // clone hooma inherits it; no other flavor may set it (stay.test.ts pins that).
    stayBooking: true,
    // Each room business may switch to By time ("day use"): Preferences › Booking style.
    bookingStyleChoice: true,
    coworkerClass: 'RoomCoworker',   // single generic coworker (Waiter-level)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    defaultUnitNames: ['1001', '1002', '1003', '1004', '2001', '2002', '2003', '2004', 'VIP1', 'VIP2'],
    defaultUnitSeats: [4, 4, 4, 4, 8, 8, 10, 10, 2, 2], // beds: doubles · family · singles
    addUnitPrefix: 'RM', // "+ Add" unit → RM1..RM9, RMA… //FLV_UPDATE
    demoLogoSet: 'room',   // demo-logo picker folder → demo_business_images/room/
    theme: { primary: '#3a2a4d', secondary: '#c05a86', accent: '#e88fb0', dotColor: '#ddd3e6', dotBg: '#f7f4fa' }, // Deep plum + rosé
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  //flv04 spa
  'spa': {
    unit: 'SpaUnit',
    pricingUnit: 'hour',
    order: 'SpaOrder',
    plannerTitle: 'Spa Planner',
    defaultLogo: spaIcon,
    tags: TAGS_SPA,
    icon: spaIcon,
    unitIcon: 'mdi-spa',
    unitOrnaments: ORNAMENTS_SPA,
    publicTypeIconTrans: '/flavor_images/flv03_spa/type_spa_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv03_spa/type_spa_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_spa_og_512x512_trans.png',
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_spa_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [30, 45, 60, 90, 120], // Server numeral 0-4 → default index 2 = 60
    toFromPicker: false,
    coworkerClass: 'SpaCoworker',   // single generic coworker (Waiter-level)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    defaultUnitNames: ['SPA1', 'SPA2', 'SPA3', 'SPA4', 'SPA5', 'SPA6', 'JCZ1', 'JCZ2', 'JCZ3', 'JCZ4'],
    defaultUnitSeats: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1], // one client per treatment station
    addUnitPrefix: 'SPA', // "+ Add" unit → SPA1..SPA9, SPAA… //FLV_UPDATE
    demoLogoSet: 'spa',   // demo-logo picker folder → demo_business_images/spa/
    theme: { primary: '#123f3a', secondary: '#1fa89a', accent: '#7fd4c8', dotColor: '#cfe4e0', dotBg: '#eff7f5' }, // Deep teal + turquoise
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  //flv05 nail
  'nail': {
    unit: 'NailUnit',
    pricingUnit: 'hour',
    order: 'NailOrder',
    plannerTitle: 'Nail Planner',
    defaultLogo: nailIcon,
    tags: TAGS_NAIL,
    icon: nailIcon,
    unitIcon: 'mdi-hand-back-left',
    unitOrnaments: ORNAMENTS_NAIL,
    publicTypeIconTrans: '/flavor_images/flv04_nail/type_nail_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv04_nail/type_nail_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_nail_og_512x512_trans.png',
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_nail_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [30, 45, 60, 90, 120], // Server numeral 0-4 → default index 2 = 60
    toFromPicker: false,
    coworkerClass: 'NailCoworker',   // single generic coworker (Waiter-level)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    defaultUnitNames: ['POS1', 'POS2', 'POS3', 'POS4', 'POS5', 'POS6', 'POS7', 'POS8', 'NAL1', 'NAL2'],
    defaultUnitSeats: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1], // one client per station
    addUnitPrefix: 'POS', // "+ Add" unit → POS1..POS9, POSA… //FLV_UPDATE
    demoLogoSet: 'nail',   // demo-logo picker folder → demo_business_images/nail/
    theme: { primary: '#2b1a2e', secondary: '#ec4899', accent: '#f9a8d4', dotColor: '#ead9e6', dotBg: '#faf5f9' }, // Near-black plum + hot fuchsia
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  //flv06 yacht
  'yacht': {
    // Booking style (owner, 2026-10-27): a business may book By days instead of By time —
    // counted in DAYS with pick-up / return at 09:00 unless it sets its own. See src/utils/stay.ts.
    bookingStyleChoice: true,
    stayUnit: 'day',
    checkInTime: '09:00',
    checkOutTime: '09:00',
    unit: 'YachtUnit',
    pricingUnit: 'hour',
    order: 'YachtOrder',
    plannerTitle: 'Yacht Planner',
    defaultLogo: yachtIcon,
    tags: TAGS_YACHT,
    icon: yachtIcon,
    unitIcon: 'mdi-sail-boat',
    unitOrnaments: ORNAMENTS_YACHT,
    publicTypeIconTrans: '/flavor_images/flv05_yacht/type_yacht_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv05_yacht/type_yacht_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_yacht_og_512x512_trans.png',
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_yacht_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [60, 360, 1440, 2880, 4320], // Server is numeral 0 1 2 3 4 -> Default is 2 = 1440
    toFromPicker: true,
    coworkerClass: 'YachtCoworker',   // single generic coworker (Waiter-level)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    defaultUnitNames: ['BT01', 'BT02', 'BT03', 'BT04', 'YCH1', 'YCH2', 'YCH3', 'YCH4', 'JET1', 'JET2'],
    defaultUnitSeats: [6, 6, 8, 8, 10, 10, 12, 12, 2, 2], // passengers
    addUnitPrefix: 'BT', // "+ Add" unit → BT1..BT9, BTA… //FLV_UPDATE
    demoLogoSet: 'yacht',   // demo-logo picker folder → demo_business_images/yacht/
    theme: { primary: '#0d4d73', secondary: '#2b7fb3', accent: '#f6bd3b', dotColor: '#c3ddec', dotBg: '#eef6fb' }, // Yacht — ocean blue + sun yellow (blue dot-grid)
    // theme: { primary: '#0d4d73', secondary: '#2b7fb3', accent: '#e8a317', dotColor: '#c3ddec', dotBg: '#eef6fb' },
    // theme: { primary: '#0d4d73', secondary: '#2b7fb3', accent: '#f0a500', dotColor: '#c3ddec', dotBg: '#eef6fb' },
    // theme: { primary: '#0d4d73', secondary: '#2b7fb3', accent: '#ff7043', dotColor: '#c3ddec', dotBg: '#eef6fb' },

    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  //flv07 hair
  'hair': {
    unit: 'HairUnit',
    pricingUnit: 'hour',
    order: 'HairOrder',
    plannerTitle: 'Hair Salon Planner',
    defaultLogo: hairIcon,
    tags: TAGS_HAIR,
    icon: hairIcon,
    unitIcon: 'mdi-content-cut',
    unitOrnaments: ORNAMENTS_HAIR,
    publicTypeIconTrans: '/flavor_images/flv07_hair/type_hair_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv07_hair/type_hair_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_hair_og_512x512_trans.png',
    durationOptions: [30, 45, 60, 90, 120], // Server numeral 0-4 → default index 2
    toFromPicker: false,
    coworkerClass: 'HairCoworker',   // single generic coworker (Waiter-level)
    // ⚠️ THE UNIT IS A PERSON. A hair unit is a stylist, not a place — the
    // salon renames STY1… to real names. One seat each, because a
    // stylist serves one client at a time.
    defaultUnitNames: ['STY1', 'STY2', 'STY3', 'STY4', 'STY5', 'STY6'],
    defaultUnitSeats: [1, 1, 1, 1, 1, 1],
    addUnitPrefix: 'STY', // "+ Add" unit → STY1..STY9, STYA… //FLV_UPDATE
    theme: { primary: '#4a3428', secondary: '#a1724e', accent: '#d9b08c', dotColor: '#e6d7c9', dotBg: '#faf6f2' },
  },
  //flv08 dent
  'dent': {
    unit: 'DentistUnit',
    order: 'DentistOrder',
    plannerTitle: 'Dental Clinic Planner',
    defaultLogo: dentIcon,
    tags: TAGS_DENT,
    icon: dentIcon,
    unitIcon: 'mdi-tooth',
    unitOrnaments: ORNAMENTS_DENT,
    publicTypeIconTrans: '/flavor_images/flv08_dent/type_dent_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv08_dent/type_dent_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_dent_og_512x512_trans.png',
    durationOptions: [15, 30, 45, 60, 90], // Server numeral 0-4 → default index 2
    toFromPicker: false,
    coworkerClass: 'DentistCoworker',   // single generic coworker (Waiter-level)
    // ⚠️ THE UNIT IS A PERSON. A dent unit is a dentist, not a place — the
    // venue renames DR1… to real names.
    defaultUnitNames: ['DR1', 'DR2', 'DR3', 'DR4', 'HYG1', 'HYG2'],
    defaultUnitSeats: [1, 1, 1, 1, 1, 1],
    addUnitPrefix: 'DR', // "+ Add" unit → DR1..DR9, DRA… //FLV_UPDATE
    theme: { primary: '#0f4c5c', secondary: '#2a9d8f', accent: '#e9c46a', dotColor: '#cfe6e3', dotBg: '#f4fafa' },
  },
  //flv09 office
  'office': {
    unit: 'OfficeUnit',
    order: 'OfficeOrder',
    plannerTitle: 'Office Appointments Planner',
    defaultLogo: officeIcon,
    tags: TAGS_OFFICE,
    icon: officeIcon,
    unitIcon: 'mdi-account-tie',
    unitOrnaments: ORNAMENTS_OFFICE,
    publicTypeIconTrans: '/flavor_images/flv09_office/type_office_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv09_office/type_office_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_office_og_512x512_trans.png',
    durationOptions: [15, 30, 45, 60, 90], // Server numeral 0-4 → default index 2
    toFromPicker: false,
    coworkerClass: 'OfficeCoworker',   // single generic coworker (Waiter-level)
    // ⚠️ THE UNIT IS A PERSON. A office unit is a advisor, not a place — the
    // venue renames ADV1… to real names.
    defaultUnitNames: ['ADV1', 'ADV2', 'ADV3', 'ADV4', 'ADV5', 'ADV6'],
    defaultUnitSeats: [2, 2, 2, 2, 2, 2],
    addUnitPrefix: 'ADV', // "+ Add" unit → ADV1..ADV9, ADVA… //FLV_UPDATE
    theme: { primary: '#2b2d42', secondary: '#5c6b9c', accent: '#ef8354', dotColor: '#d6d9e6', dotBg: '#f6f7fb' },
  },
  //flv10 court
  'court': {
    unit: 'CourtUnit',
    pricingUnit: 'hour',
    order: 'CourtOrder',
    plannerTitle: 'Sports Court Planner',
    defaultLogo: courtIcon,
    tags: TAGS_COURT,
    icon: courtIcon,
    unitIcon: 'mdi-tennis',
    unitOrnaments: ORNAMENTS_COURT,
    publicTypeIconTrans: '/flavor_images/flv10_court/type_court_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv10_court/type_court_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_court_og_512x512_trans.png',
    durationOptions: [60, 90, 120, 150, 180], // Server numeral 0-4 → default index 2
    toFromPicker: false,
    coworkerClass: 'CourtCoworker',   // single generic coworker (Waiter-level)
    // The unit is a PLACE: a court the club lists under its own name.
    defaultUnitNames: ['TEN1', 'TEN2', 'TEN3', 'TEN4', 'PAD1', 'PAD2', 'PAD3', 'PAD4', 'BAD1', 'BAD2'],
    defaultUnitSeats: [4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
    addUnitPrefix: 'CT', // "+ Add" unit → CT1..CT9, CTA… //FLV_UPDATE
    theme: { primary: '#1b4332', secondary: '#40916c', accent: '#d4e157', dotColor: '#cfe3d5', dotBg: '#f3f8f4' },
  },
  //flv11 plane
  'plane': {
    // Booking style (owner, 2026-10-27): a business may book By days instead of By time —
    // counted in DAYS with pick-up / return at 09:00 unless it sets its own. See src/utils/stay.ts.
    bookingStyleChoice: true,
    stayUnit: 'day',
    checkInTime: '09:00',
    checkOutTime: '09:00',
    unit: 'PlaneUnit',
    pricingUnit: 'hour',
    order: 'PlaneOrder',
    plannerTitle: 'Aviation School Planner',
    defaultLogo: planeIcon,
    tags: TAGS_PLANE,
    icon: planeIcon,
    unitIcon: 'mdi-airplane',
    unitOrnaments: ORNAMENTS_PLANE,
    publicTypeIconTrans: '/flavor_images/flv11_plane/type_plane_og_512x512_trans.png',
    defaultBusinessLogo: '/flavor_images/flv11_plane/type_plane_og_512x512.png',
    cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_plane_og_512x512_trans.png',
    durationOptions: [30, 60, 90, 120, 180], // Server numeral 0-4 → default index 2
    toFromPicker: false,
    coworkerClass: 'PlaneCoworker',   // single generic coworker (Waiter-level)
    // The unit is a PLACE: an aircraft the club lists under its own name. ⚠️ 4 characters max, like
    // every unit name (was 'C152-1'…, saved as-is on create — owner 2026-10-30): type + letter.
    defaultUnitNames: ['C52A', 'C52B', 'C72A', 'C72B', 'C72C', 'P28A', 'P28B', 'D40A', 'SIM1', 'SIM2'],
    defaultUnitSeats: [2, 2, 4, 4, 4, 4, 4, 4, 1, 1],
    addUnitPrefix: 'AC', // "+ Add" unit → AC1..AC9, ACA… //FLV_UPDATE
    theme: { primary: '#14213d', secondary: '#3d5a80', accent: '#fca311', dotColor: '#d3dbe8', dotBg: '#f5f7fb' },
  },

  // ── PLACEHOLDER flavor ──────────────────────────────────────────────────────
  // Rename 'newflv' and fill these in. Currently reuses the table Parse classes
  // (Table/Order) + table logo/icon/tags so it compiles and runs; swap to its own
  // classes/assets when ready (add new ones to UnitClassName/OrderClassName in types.ts).
  'newflv': {
    // ── CANONICAL FIELD SET (FlavorSetting order) — every flavor lists ALL of these,
    //    same order. Required lines are active; optional lines stay commented until used. ──
    unit: 'Table',                                            // TODO: own UnitClassName (e.g. 'NewflvUnit') — add to types.ts
    order: 'Order',                                           // TODO: own OrderClassName — add to types.ts + server plannerOrderTypes
    plannerTitle: 'New Flavor Planner',                       // {flvPlannerTitle} i18n token + login subtitle
    defaultLogo: restaurantIcon,                              // TODO: import '/flavor_images/newflv/flavor_default_icon.svg'
    tags: TAGS_TABLE,                                         // TODO: own TAGS_NEWFLV (6 mdi, order = locale tag01..06)
    icon: restaurantIcon,                                     // reuses the flavor logo (icon == defaultLogo)
    unitIcon: 'mdi-help-box',                                 // MDI glyph for the units-column header
    // unitOrnaments: ORNAMENTS_NEWFLV,                       // ~6 attachable unit icons (omit → feature off)
    // publicTypeIconTrans: '/flavor_images/newflv/type_newflv_og_512x512_trans.png', // transparent flavor mark — see scripts/og-cards.py
    // cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_newflv_og_512x512_trans.png', // same file on the CDN
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_newflv_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [15, 30, 60, 90, 120, 1440, 2880, 4320], // booking presets (min); reserve_max index, default 2
    toFromPicker: false,                                      // false = duration presets · true = manual From…To (rentals)
    // coworkerClass: 'NewflvCoworker',                       // single generic coworker (omit → 3-role restaurant model)
    // stripeBtns: [{ id: '', key: '' }],                     // per-flavor Stripe buttons
    // defaultUnitNames: ['U1','U2','U3','U4','U5','U6','U7','U8','U9','U10'], // wizard unit names (omit → table presets)
    defaultUnitSeats: [2, 2, 2, 4, 4, 4, 6, 6, 10, 15],       // capacity per default unit (parallel to names)
    // addUnitPrefix: 'U',   // "+ Add" unit prefix, ≤3 chars (omit → 'TAB')
    // demoLogoSet: 'newflv',                                 // demo-logo picker folder (omit → shares 'table')
    // theme: { primary: '#000000', secondary: '#000000', accent: '#000000', dotColor: '#cdd9e6', dotBg: '#f3f7fb' }, // brand colors + optional client dot-grid tint (omit → DEFAULT_THEME / SCSS default)
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
  // Test/demo flavor — kept LAST.
  'monster': {
    unit: 'MonsterUnit',
    order: 'MonsterOrder',
    plannerTitle: 'Monster Planner',
    defaultLogo: monsterIcon,
    tags: TAGS_CAR,
    icon: monsterIcon,
    unitIcon: 'mdi-emoticon-devil',
    unitOrnaments: ORNAMENTS_MONSTER,
    // publicTypeIconTrans: '/flavor_images/monster/type_monster_og_512x512_trans.png', // test flavor — no mark exported
    // cdnTypeIconTrans: 'https://cdn.dreamdiner.io/brand_logos/type_monster_og_512x512_trans.png',
    // storeLogoUrl: 'https://cdn.dreamdiner.io/brand_logos/type_monster_trans_512.png', // hi-res store logo (omit → uses icon)
    durationOptions: [60, 360, 1440, 2880, 4320], // Server is numeral 0 1 2 3 4 -> Default is 2 = 1440
    toFromPicker: true,
    // coworkerClass: 'MonsterCoworker',   // single generic coworker (omit → 3-role restaurant model)
    // stripeBtns: [{ id: '', key: '' }],   // per-flavor Stripe buttons
    // defaultUnitNames: [...],   // wizard unit names (omit → table presets)
    defaultUnitSeats: [2, 2, 2, 4, 4, 4, 6, 6, 10, 15],
    // demoLogoSet: 'monster',   // demo-logo picker folder (omit → shares 'table')
    // theme: { primary: '#000000', secondary: '#000000', accent: '#000000' }, // brand colors (omit → DEFAULT_THEME)
    // userRegistered_using: 'custom',   // override flavor tag in registered_using (omit → uses BUSINESS_TYPE)
  },
}

/**
 * CLONE FLAVORS — clone key → the flavor whose CONTENT it is.
 *
 * A clone is a separate app in the stores (its own bundle id, listing, RevenueCat app)
 * running the same vertical: `wellza` is `spa` with a different name on the icon. It
 * therefore needs no settings, no tags, no vocabulary and no images of its own, and
 * giving it any would only let the two drift.
 *
 * ⚠️ This is the ONE place a clone is declared. `FLAVOR_SETTING` (below),
 * `flavorImageDir()`, `useTagsLabels()` and the i18n boot all read it, so a clone
 * added here needs no other code change — which is the entire point: cloning a flavor
 * must cost minutes, not a day of i18n.
 */
export const FLAVOR_CLONE_OF: Partial<Record<BusinessType, BusinessType>> = {
  wellza: 'spa',
  neela: 'nail',   // prm050, 2026-10-19
  hooma: 'room',   // prm050, 2026-10-22
}

/**
 * The few settings a clone does NOT inherit.
 *
 * A clone is its base in every respect except the ones listed here — keep this as small
 * as it can be, because every field added is a way for the two to drift. `plannerTitle`
 * is the obvious one: it is the app's own name, shown under the logo on the login page
 * and substituted into {flvPlannerTitle} throughout the copy, so a Wellza user must not
 * read "Spa Planner" there.
 */
export const FLAVOR_CLONE_OVERRIDES: Partial<Record<BusinessType, Partial<FlavorSetting>>> = {
  wellza: {
    plannerTitle: 'Wellza',
    // Spa's colours, but a light-violet canvas and dot grid so Wellza reads as its own
    // brand (owner, 2026-09-26). ⚠️ `theme` is replaced WHOLE by the clone merge below, so
    // it spreads spa's theme first — overriding only dotBg/dotColor would drop the rest.
    theme: { ...(FLAVOR_SETTING_OWN as Record<string, FlavorSetting>).spa.theme, dotBg: '#f5f2fc', dotColor: '#ddd4f0' },
  },
  neela: {
    plannerTitle: 'Neela',
  },
  hooma: {
    plannerTitle: 'Hooma',
  },
}

/**
 * Every flavor's settings, clones resolved. A clone's entry IS its base's object —
 * same reference, so a change to the base reaches the clone by construction.
 */
export const FLAVOR_SETTING: Record<BusinessType, FlavorSetting> = {
  ...(FLAVOR_SETTING_OWN as Record<BusinessType, FlavorSetting>),
  ...Object.fromEntries(
    Object.entries(FLAVOR_CLONE_OF).map(([clone, base]) => [
      clone,
      {
        ...(FLAVOR_SETTING_OWN as Record<string, FlavorSetting>)[base as string],
        ...(FLAVOR_CLONE_OVERRIDES[clone as BusinessType] || {}),
      },
    ]),
  ),
}

// Public folder under /flavor_images/ that holds a flavor's RUNTIME assets served by
// URL (splash lottie) — NOT the bundled logo/icon imports above. (The per-flavor
// "presentor" figure lived here too; retired with the Select-your-business redesign.)
// General flavors live in an flvNN_ prefixed folder (see public/flavor_images/); every
// other flavor uses its lowercase key. Keep in sync with the folder names on disk.
// //FLV_UPDATE
const FLAVOR_IMAGE_DIRS: Record<string, string> = {
  plane: 'flv11_plane',
  court: 'flv10_court',
  office: 'flv09_office',
  dent: 'flv08_dent',
  hair: 'flv07_hair',
  car: 'flv01_car',
  room: 'flv02_room',
  spa: 'flv03_spa',
  nail: 'flv04_nail',
  yacht: 'flv05_yacht',
  tableDDSL: 'table', // DreamDiner clone — reuses table's splash assets
}
// Clones inherit their base's folder, so FLAVOR_IMAGE_DIRS never needs a row per clone.
for (const [clone, base] of Object.entries(FLAVOR_CLONE_OF)) {
  FLAVOR_IMAGE_DIRS[clone] = FLAVOR_IMAGE_DIRS[base as string] || (base as string)
}
/** Folder under /flavor_images/ for a flavor's runtime assets (splash). */
export function flavorImageDir(flavor: string): string {
  return FLAVOR_IMAGE_DIRS[flavor] || String(flavor).toLowerCase()
}

/**
 * Whether a flavor can hold bookings that span MORE THAN ONE DAY. Derived — there
 * is no separate flag: true if the flavor offers the manual From…To override
 * (toFromPicker) OR any of its quick presets is ≥ 1 day (1440 min). This replaces
 * the old `timePicker === 'start-end'` switch and drives: the grid's end-day
 * (disableEndDay = !supportsMultiDay), the slot-fetch mode, the save formula, and
 * the preset→end wiring.
 */
/**
 * Does this flavor get Reserve with Google?
 *
 * Google's Actions Center vertical we are onboarded to is DINING RESERVATIONS, and the
 * partner approval covers restaurants only. On car / spa / room / nail / yacht the
 * whole feature is a dead end: their bookings are Appointments or Hotels verticals,
 * which are separate onboardings we have not applied for. So the dashboard block, the
 * announcement popup and the dialog all hang off this one flag rather than repeating a
 * `=== 'table'` test in three places and drifting apart.
 *
 * ⚠️ Deliberately narrower than the merchant feed, which also carries tableDDSL,
 * tablePL and the legacy restaurantDD. Those venues can be FED (they are restaurants
 * and their action links resolve), but their Planner builds do not offer the opt-in yet
 * — so in practice only `table` owners can consent. Widen here first if that changes.
 *
 * BUSINESS_TYPE is fixed at build time, so this needs no reactivity.
 */
export function isRwgAllowed(flavor: string = process.env.BUSINESS_TYPE || 'table'): boolean {
  return flavor === 'table';
}

/**
 * Stand-in avatar for a business whose owner never uploaded a logo.
 *
 * ⚠️ Use this instead of importing '/logos/dd_logo_round_blue.svg' directly. That file
 * is the DREAMDINER logo — correct for `table`/`tableDDSL`, and plainly wrong on a car
 * rental or a nail salon, which is exactly the bug this replaced: every logo-less venue
 * on car.bookingsplanner.com showed a DreamDiner mark.
 *
 * Fallback order, and the reason for it:
 *   1. `defaultBusinessLogo` — the flavor's coloured mark on a neutral grey disc. This is
 *      the one built to BE a business avatar: art scaled to a uniform weight so every
 *      flavor reads equally at 32px.
 *   2. `defaultLogo` — the flat pictogram. Only reached by a flavor that ships no OG art
 *      yet (newflv, monster), so a new flavor still gets something sane on day one.
 *   3. table's pictogram — unknown flavor key.
 *
 * Both are app-local on purpose. `defaultBusinessLogo` is byte-identical to the CDN
 * artwork, so pointing at cdn.dreamdiner.io would only add a request that fails offline
 * in the Capacitor builds.
 *
 * BUSINESS_TYPE is fixed at build time, so this needs no reactivity.
 */
export function flavorDefaultLogo(flavor: string = process.env.BUSINESS_TYPE || 'table'): string {
  const setting = (FLAVOR_SETTING as Record<string, FlavorSetting | undefined>)[flavor];
  return setting?.defaultBusinessLogo || setting?.defaultLogo || FLAVOR_SETTING.table.defaultLogo;
}

export function supportsMultiDay(flavor: FlavorSetting): boolean {
  return flavor.toFromPicker || flavor.durationOptions.some((m) => m >= 1440);
}

/**
 * Flavors whose UNIT is rented by TIME, and can therefore carry a price per hour: car,
 * room, spa, nail, yacht.
 *
 * ⚠️ Reads the flavor's own `byHour` flag. It was previously DERIVED from "has its own
 * unit class", which happens to be true of every rental flavor today but states nothing
 * about intent — a future flavor with its own class that is sold per visit would have
 * started charging by the hour without anyone deciding so. A new flavor now opts in.
 *
 * Used to decide whether the unit editor shows a Price per hour, and whether Collect
 * Payments offers the calculated deposit (unit rate x hours x percentage) alongside the
 * flat sum.
 */
export function supportsHourlyPricing(flavor: FlavorSetting): boolean {
  return flavor.pricingUnit === 'hour' || flavor.pricingUnit === 'day';
}

/** 'hour' | 'day' | null — what one unit of this flavor's rate BUYS. Null when the
 *  flavor is not time-priced at all, which is what supportsHourlyPricing() tests. */
export function unitPricingPeriod(flavor: FlavorSetting): 'hour' | 'day' | null {
  return flavor.pricingUnit || null;
}

/**
 * The booking-duration options a business actually USES: its own overrides
 * (Business.custom_durations, minutes) when set & valid, else the flavor's default
 * presets. `reserve_max` is an INDEX into whatever this returns, so read durations
 * through here everywhere. Non-array / empty / all-invalid custom → fallback.
 */
export function resolveDurations(custom: unknown, fallback: number[]): number[] {
  const arr = Array.isArray(custom)
    ? custom.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0)
    : []
  return arr.length ? arr : fallback
}

/**
 * SINGLE SOURCE for the default name of a unit added beyond the presets via "+ Add".
 * Used by BOTH the create wizard (CreateBusiness.vue) and the live Tables dialog
 * (Tables.vue) so added units are named identically and per-flavor. Prefix comes from
 * FLAVOR_SETTING.addUnitPrefix (fallback 'TAB'); suffix is 1-9 then A-Z. `count` is the
 * 1-based position of the new unit (existing count + 1). Capped at 4 chars. //FLV_UPDATE
 */
export function buildAddedUnitName(count: number): string {
  const prefix = FLAVOR_SETTING[(process.env.BUSINESS_TYPE || 'table') as BusinessType]?.addUnitPrefix || 'TAB'
  if (count <= 9) return `${prefix}${count}`.slice(0, 4)
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const i = count - 10
  if (i >= 0 && i < alphabet.length) return `${prefix}${alphabet[i]}`.slice(0, 4)
  return `${prefix}${count}`.slice(0, 4).toUpperCase()
}
